import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthScene } from '@/versions/v1/modules/auth/AuthScene';
import { LobbyDeclinedScene, LobbyScene } from '@/versions/v1/modules/auth/LobbyScene';
import { SignupScene } from '@/versions/v1/modules/auth/SignupScene';
import { DocsLibraryScene } from '@/versions/v1/modules/docs-library/DocsLibraryScene';
import { ChangelogScene } from '@/versions/v1/modules/changelog/ChangelogScene';
import { NotFoundScene } from '@/versions/v1/modules/NotFoundScene';
import { ConnectorDetailScene } from '@/versions/v1/modules/settings/ConnectorDetailScene';
import { PluginDetailScene } from '@/versions/v1/modules/settings/PluginDetailScene';
import { PluginMarketplaceScene } from '@/versions/v1/modules/settings/PluginMarketplaceScene';
import { PluginSkillDetailScene } from '@/versions/v1/modules/settings/PluginSkillDetailScene';
import { PluginsScene } from '@/versions/v1/modules/settings/PluginsScene';
import { ProjectSettingsScene } from '@/versions/v1/modules/settings/ProjectSettingsScene';
import { SettingsRoute } from '@/versions/v1/modules/settings/SettingsRoute';
import { SkillDetailScene } from '@/versions/v1/modules/settings/SkillDetailScene';
import { WorkspaceChatScene } from '@/versions/v1/modules/workspace/WorkspaceChatScene';
import { AdminDashboardScene } from '@/versions/v1/modules/admin/AdminDashboardScene';
import { AgentReviewInlineScene } from '@/versions/v1/modules/admin/AgentReviewInlineScene';
import { ApiKeysScene } from '@/versions/v1/modules/admin/ApiKeysScene';
import { ConnectorsScene } from '@/versions/v1/modules/admin/ConnectorsScene';
import { EditorPageFlowScene } from '@/versions/v1/modules/admin/EditorPageFlowScene';
import { EmbeddingProviderScene } from '@/versions/v1/modules/admin/EmbeddingProviderScene';
import { PredefinedSkillsScene } from '@/versions/v1/modules/admin/PredefinedSkillsScene';
import { ProjectsScene } from '@/versions/v1/modules/admin/ProjectsScene';
import { SandboxLibrariesScene } from '@/versions/v1/modules/admin/SandboxLibrariesScene';
import { ServiceHealthScene } from '@/versions/v1/modules/admin/ServiceHealthScene';
import { SignupsScene } from '@/versions/v1/modules/admin/SignupsScene';
import { SystemPromptScene } from '@/versions/v1/modules/admin/SystemPromptScene';
import { AiModelsScene } from '@/versions/v1/modules/admin/AiModelsScene';
import { BuiltinToolsScene } from '@/versions/v1/modules/admin/BuiltinToolsScene';
import { MaintenanceScene } from '@/versions/v1/modules/admin/MaintenanceScene';
import { SsoScene } from '@/versions/v1/modules/admin/SsoScene';
import { SystemStatusScene } from '@/versions/v1/modules/admin/SystemStatusScene';
import { UsersScene } from '@/versions/v1/modules/admin/UsersScene';
import { AnnouncementBannerScene } from '@/versions/v1/modules/admin/AnnouncementBannerScene';
import { WorkspaceScene } from '@/versions/v1/modules/admin/WorkspaceScene';
import { PortfolioScene } from '@/versions/v1/modules/projects/PortfolioScene';
import { ProjectChatScene } from '@/versions/v1/modules/projects/ProjectChatScene';
import { ProjectHomeScene } from '@/versions/v1/modules/projects/ProjectHomeScene';
import { ProjectWorkflowsScene } from '@/versions/v1/modules/projects/ProjectWorkflowsScene';
import { ProjectEditorScene } from '@/versions/v1/modules/projects/ProjectEditorScene';
import { ProjectDashboardsScene } from '@/versions/v1/modules/projects/ProjectDashboardsScene';
import { ProjectTasksScene } from '@/versions/v1/modules/projects/ProjectTasksScene';
import { PlantModelScene } from '@/versions/v1/modules/ingestion/Scene';
import { CapacityScene } from '@/versions/v1/modules/capacity/Scene';
import { IngestionSetupScene } from '@/versions/v1/modules/ingestion/SetupScene';
import { OverviewScene } from '@/versions/v1/modules/overview/Scene';
import { AdvisoryScene } from '@/versions/v1/modules/advisory/Scene';
import { CasePage } from '@/versions/v1/modules/advisory/CasePage';
import { QualityScene } from '@/versions/v1/modules/quality/Scene';
import { ComplianceScene } from '@/versions/v1/modules/compliance/Scene';
import { MaintenanceScene as ProjectMaintenanceScene } from '@/versions/v1/modules/maintenance/Scene';
import { DowntimeScene as ProjectDowntimeScene } from '@/versions/v1/modules/downtime/Scene';
import { ProcessScene } from '@/versions/v1/modules/process/Scene';
import { ReportsScene } from '@/versions/v1/modules/reports/Scene';
import { MetalAccountingScene } from '@/versions/v1/modules/metal-accounting/Scene';
import { PlanningScene } from '@/versions/v1/modules/planning/Scene';
import { RiskScene } from '@/versions/v1/modules/risk/Scene';
import { CostScene } from '@/versions/v1/modules/cost/Scene';
import { DailyMeetingScene } from '@/versions/v1/modules/meeting/Scene';
import { DocumentsScene } from '@/versions/v1/modules/documents/Scene';
import { AccessDeniedPanel } from '@/versions/v1/modules/_chrome/AccessDeniedPanel';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { AppLayout } from './layout';

function CostRoute() {
  const { can } = useRole();
  if (!can('money:view')) {
    return <AccessDeniedPanel resource="Cost & revenue" />;
  }
  return <CostScene />;
}

function ReportingToOverviewRedirect() {
  const location = useLocation();
  return <Navigate to={`../overview${location.search}`} replace relative="path" />;
}

export function Router() {
  return (
    <Routes>
      <Route path="/login" element={<AuthScene />} />
      <Route path="/signup" element={<SignupScene />} />
      <Route path="/lobby" element={<LobbyScene />} />
      <Route path="/lobby/declined" element={<LobbyDeclinedScene />} />

      <Route element={<AppLayout />}>
        <Route path="/" element={<WorkspaceChatScene />} />
        <Route path="/changelog" element={<ChangelogScene />} />
        <Route path="/docs/logic" element={<DocsLibraryScene />} />
        <Route path="/docs/logic/:module/:doc" element={<DocsLibraryScene />} />

        <Route path="/settings" element={<Navigate to="/settings/connectors" replace />} />
        <Route path="/settings/connectors/:connectorId" element={<ConnectorDetailScene />} />
        <Route path="/settings/plugins/marketplace" element={<PluginMarketplaceScene />} />
        <Route path="/settings/plugins/:pluginId" element={<PluginDetailScene />} />
        <Route path="/settings/plugins" element={<PluginsScene />} />
        <Route path="/settings/projects/:projectId" element={<ProjectSettingsScene />} />
        <Route
          path="/settings/skills/plugin/:pluginId/:name"
          element={<PluginSkillDetailScene />}
        />
        <Route path="/settings/skills/:slug" element={<SkillDetailScene />} />
        <Route path="/settings/:slug" element={<SettingsRoute />} />

        <Route path="/admin" element={<AdminDashboardScene />} />
        <Route path="/admin/users" element={<UsersScene />} />
        <Route path="/admin/signups" element={<SignupsScene />} />
        <Route path="/admin/projects" element={<ProjectsScene />} />
        <Route path="/admin/connectors" element={<ConnectorsScene />} />
        <Route path="/admin/system-prompt" element={<SystemPromptScene />} />
        <Route path="/admin/predefined-skills" element={<PredefinedSkillsScene />} />
        <Route path="/admin/sandbox-libraries" element={<SandboxLibrariesScene />} />
        <Route path="/admin/embedding-provider" element={<EmbeddingProviderScene />} />
        <Route path="/admin/workspace" element={<WorkspaceScene />} />
        <Route path="/admin/agent-review-inline" element={<AgentReviewInlineScene />} />
        <Route path="/admin/editor-page-flow" element={<EditorPageFlowScene />} />
        <Route path="/admin/api-keys" element={<ApiKeysScene />} />
        <Route path="/admin/service-health" element={<ServiceHealthScene />} />
        <Route path="/admin/system-status" element={<SystemStatusScene />} />
        <Route path="/admin/sso" element={<SsoScene />} />
        <Route path="/admin/maintenance" element={<MaintenanceScene />} />
        <Route path="/admin/ai-models" element={<AiModelsScene />} />
        <Route
          path="/admin/claude-models"
          element={<Navigate to="/admin/ai-models" replace />}
        />
        <Route
          path="/admin/google-models"
          element={<Navigate to="/admin/ai-models" replace />}
        />
        <Route path="/admin/builtin-tools" element={<BuiltinToolsScene />} />
        <Route path="/admin/announcement-banner" element={<AnnouncementBannerScene />} />

        <Route path="/projects" element={<PortfolioScene />} />
        <Route path="/projects/:projectId" element={<ProjectHomeScene />} />
        <Route path="/projects/:projectId/chat" element={<ProjectChatScene />} />
        <Route
          path="/projects/:projectId/workflows"
          element={<ProjectWorkflowsScene />}
        />
        <Route
          path="/projects/:projectId/editor"
          element={<ProjectEditorScene />}
        />
        <Route
          path="/projects/:projectId/dashboards"
          element={<ProjectDashboardsScene />}
        />
        <Route
          path="/projects/:projectId/tasks"
          element={<ProjectTasksScene />}
        />
        {/* fork: routes — OptiFlow function rail (function-rail-restructure F0) */}
        <Route
          path="/projects/:projectId/plant-model"
          element={<PlantModelScene />}
        />
        <Route
          path="/projects/:projectId/capacity"
          element={<CapacityScene />}
        />
        <Route
          path="/projects/:projectId/ingestion"
          element={<IngestionSetupScene />}
        />
        <Route path="/projects/:projectId/overview" element={<OverviewScene />} />
        <Route
          path="/projects/:projectId/reporting"
          element={<ReportingToOverviewRedirect />}
        />
        <Route
          path="/projects/:projectId/advisory/case/:caseId"
          element={<CasePage />}
        />
        <Route
          path="/projects/:projectId/advisory"
          element={<AdvisoryScene />}
        />
        {/* Path-segment tab (e.g. /advisory/driver-tree) — a direct open or
            reload must land on the surface, not just the client-side `?tab=`
            alias (fixer round 1 MUST-FIX 3). AdvisoryScene reads the `tab`
            param and falls back to `queue` for an unknown segment. */}
        <Route
          path="/projects/:projectId/advisory/:tab"
          element={<AdvisoryScene />}
        />
        <Route
          path="/projects/:projectId/process"
          element={<ProcessScene />}
        />
        <Route
          path="/projects/:projectId/quality"
          element={<QualityScene />}
        />
        <Route
          path="/projects/:projectId/metal-accounting"
          element={<MetalAccountingScene />}
        />
        <Route
          path="/projects/:projectId/compliance"
          element={<ComplianceScene />}
        />
        <Route path="/projects/:projectId/risk" element={<RiskScene />} />
        <Route
          path="/projects/:projectId/maintenance"
          element={<ProjectMaintenanceScene />}
        />
        <Route
          path="/projects/:projectId/downtime"
          element={<ProjectDowntimeScene />}
        />
        <Route
          path="/projects/:projectId/reports"
          element={<ReportsScene />}
        />
        <Route
          path="/projects/:projectId/planning"
          element={<PlanningScene />}
        />
        <Route path="/projects/:projectId/cost" element={<CostRoute />} />
        <Route path="/projects/:projectId/meeting" element={<DailyMeetingScene />} />
        {/* Path-segment tab (e.g. /meeting/shift-handover), Advisory's idiom: a
            deep link lands on that view; an unknown segment keeps the default. */}
        <Route path="/projects/:projectId/meeting/:tab" element={<DailyMeetingScene />} />
        <Route path="/projects/:projectId/documents" element={<DocumentsScene />} />

        <Route path="*" element={<NotFoundScene />} />
      </Route>
    </Routes>
  );
}
